import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck,
  Building2,
  ExternalLink,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  Clock,
  Info,
  Pencil,
  Mail,
  MessageSquare,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useMyMembershipOverview,
  useClaimMembership,
  type MembershipOverviewItem,
} from '@/api/extensions/useMembershipVerificationOps';
import { useToast } from '@/contexts/ToastContext';
import { SUPPORT_MAILTO } from '@/constants/support';

/**
 * Organization Memberships tab on the profile page.
 *
 * Why this exists: contestants kept missing the inline "I'm already a member"
 * field during event registration, paid the higher non-member price, and got
 * frustrated. This tab lets them sort their membership out *ahead* of
 * registering — for every org running a membership program they can:
 *   - see their membership number and its status (pending / approved-live),
 *   - claim a number if they already have one (an admin/delegate then
 *     approves or rejects it — the same trust-path flow used at registration),
 *   - jump to the org's signup page if they are not a member yet.
 *
 * Only orgs with allow_applications = true appear here (plus any org the user
 * already belongs to), so test orgs stay hidden until an admin opts them in.
 *
 * The underlying query polls (see useMyMembershipOverview) so an admin's
 * approve/reject is reflected here without a manual page refresh.
 */
export function MembershipsTab() {
  const { data, isLoading, error } = useMyMembershipOverview();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Could not load your organization memberships. Refresh the page to
            try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-primary" />
            Organization memberships
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Save your membership number for each organization here so you get
            the member price the moment you register for an event — no need to
            hunt for the field mid-registration.
          </p>
        </CardHeader>
      </Card>

      {data.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              icon={Building2}
              title="No organizations to show yet"
              description="No organizations are currently accepting membership numbers. Check back later, or contact your organization directly."
            />
          </CardContent>
        </Card>
      ) : (
        data.map((item) => (
          <OrganizationMembershipCard key={item.organization_id} item={item} />
        ))
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              Claimed numbers are trusted right away, so you can register at the
              member price. An admin or delegate then confirms the number — if
              it can't be verified the claim is rejected and the non-member fee
              applies.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** A single organization row: status, member number, claim form, signup link. */
function OrganizationMembershipCard({ item }: { item: MembershipOverviewItem }) {
  const claimMembership = useClaimMembership();
  const { showSuccess, showError } = useToast();
  const [memberNumber, setMemberNumber] = useState(item.member_number ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);
  // Pending claims show the number read-only by default; the pencil flips
  // this on so the user can correct it while it's still awaiting review.
  const [isEditing, setIsEditing] = useState(false);

  const isApproved = item.membership_status === 'approved';
  const isPending = item.has_membership && item.membership_status === 'pending';
  const isRejected = item.has_membership && item.membership_status === 'rejected';
  // A rejected row still has has_membership=true, so notClaimed only covers
  // users who have never submitted a number.
  const notClaimed = !item.has_membership;
  const orgName = item.organization_name ?? 'Organization';

  const trimmed = memberNumber.trim();
  const unchanged = trimmed === (item.member_number ?? '').trim();
  const canSave = trimmed.length > 0 && !unchanged && !claimMembership.isPending;

  const startEditing = () => {
    setMemberNumber(item.member_number ?? '');
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setMemberNumber(item.member_number ?? '');
    setSaveError(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (trimmed.length === 0) {
      setSaveError('Enter your member number first.');
      return;
    }
    setSaveError(null);
    try {
      await claimMembership.mutateAsync({
        organizationId: item.organization_id,
        memberNumber: trimmed,
      });
      showSuccess(
        'Member number saved',
        `Your number for ${orgName} is pending review.`,
      );
      // Drop back to the read-only pending view; the polled query refresh
      // brings the corrected number down.
      setIsEditing(false);
    } catch (err: any) {
      const msg =
        err?.body?.error ??
        err?.message ??
        'Could not save your member number. Please try again.';
      setSaveError(msg);
      showError('Could not save member number', msg);
    }
  };

  // The editable input + action buttons, shared by the "not claimed" claim
  // form and the "pending" inline-edit form.
  const renderClaimForm = (showCancel: boolean) => (
    <div className="space-y-2">
      <Label
        htmlFor={`member-number-${item.organization_id}`}
        className="text-xs"
      >
        Member / card number
      </Label>
      <div className="flex items-stretch gap-2">
        <Input
          id={`member-number-${item.organization_id}`}
          type="text"
          autoComplete="off"
          placeholder="e.g. 12345"
          value={memberNumber}
          onChange={(e) => {
            setMemberNumber(e.target.value);
            if (saveError) setSaveError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) {
              e.preventDefault();
              handleSave();
            }
          }}
          className="h-9 text-sm flex-1"
          disabled={claimMembership.isPending}
          autoFocus={showCancel}
        />
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          className="h-9 shrink-0"
        >
          {claimMembership.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Saving…
            </>
          ) : showCancel ? (
            'Update number'
          ) : (
            'Claim membership'
          )}
        </Button>
        {showCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={cancelEditing}
            disabled={claimMembership.isPending}
            className="h-9 shrink-0"
          >
            Cancel
          </Button>
        )}
      </div>
      {saveError && <p className="text-xs text-destructive">{saveError}</p>}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{orgName}</span>
          </CardTitle>
          <MembershipStatusBadge item={item} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Approved — green read-only block */}
        {isApproved && (
          <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3">
            <p className="text-xs text-muted-foreground">Member number</p>
            <p className="text-sm font-semibold text-foreground mt-0.5">
              {item.member_number ?? '—'}
            </p>
            <p className="text-xs text-green-700 dark:text-green-300 flex items-start gap-1 mt-2">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Your membership is approved and live. You'll get the member
                price automatically when you register.
              </span>
            </p>
          </div>
        )}

        {/* Pending — yellow read-only block with a pencil to edit inline */}
        {isPending && !isEditing && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Member number</p>
                <p className="text-sm font-semibold text-foreground mt-0.5 break-words">
                  {item.member_number ?? '—'}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={startEditing}
                className="h-8 px-2 shrink-0 text-amber-700 hover:text-amber-800 dark:text-amber-300"
                aria-label="Edit member number"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1 mt-2">
              <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Awaiting review by an admin — you'll get the member price in the
                meantime. Use the pencil to correct it before it's approved.
              </span>
            </p>
          </div>
        )}

        {/* Pending — inline edit form (yellow box) */}
        {isPending && isEditing && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
            {renderClaimForm(true)}
          </div>
        )}

        {/* Rejected — red read-only block, resubmission is blocked */}
        {isRejected && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
            <p className="text-xs text-muted-foreground">Submitted member number</p>
            <p className="text-sm font-semibold text-foreground mt-0.5 break-words line-through decoration-red-500/60">
              {item.member_number ?? '—'}
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 flex items-start gap-1 mt-2">
              <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                This number was rejected. A rejected number can't be replaced
                here — contact {orgName} or HighCall support to get a valid
                membership number approved.
              </span>
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button asChild type="button" variant="outline" size="sm">
                <a href={SUPPORT_MAILTO}>
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Email support
                </a>
              </Button>
              <Button asChild type="button" variant="outline" size="sm">
                <Link to="/support">
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Submit a support request
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Not claimed — plain claim form */}
        {notClaimed && (
          <>
            {renderClaimForm(false)}
            <p className="text-xs text-muted-foreground">
              Already a member? Enter your number above. Not a member yet? Use
              the signup link below.
            </p>
          </>
        )}

        {/* Signup / website link */}
        {item.signup_url && (
          <div
            className={`flex items-center gap-3 border-t border-border pt-3 ${
              isApproved ? 'justify-end' : 'justify-between'
            }`}
          >
            {!isApproved && (
              <p className="text-xs text-muted-foreground">
                Not a member of {orgName} yet?
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(item.signup_url!, '_blank', 'noopener,noreferrer')
              }
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              {isApproved ? 'Go to website' : 'Sign up'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Status pill: Not claimed / Pending approval / Approved · Live. */
function MembershipStatusBadge({ item }: { item: MembershipOverviewItem }) {
  if (!item.has_membership) {
    return (
      <Badge
        variant="outline"
        className="shrink-0 bg-gray-100 text-gray-600 border-gray-200"
      >
        Not claimed
      </Badge>
    );
  }
  if (item.membership_status === 'approved') {
    return (
      <Badge
        variant="outline"
        className="shrink-0 bg-green-100 text-green-800 border-green-200"
      >
        Approved · Live
      </Badge>
    );
  }
  if (item.membership_status === 'rejected') {
    return (
      <Badge
        variant="outline"
        className="shrink-0 bg-red-100 text-red-800 border-red-200"
      >
        Rejected
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="shrink-0 bg-yellow-100 text-yellow-800 border-yellow-200"
    >
      Pending approval
    </Badge>
  );
}
